import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bookmark, Clock, History, Pencil, Send, Trash2 } from "lucide-react";
import {
    useGetTelegramBotsQuery,
    useGetBatchStatusQuery,
    useCancelMassMessageBatchMutation,
    useGetMessagingCampaignsQuery,
    useStoreMessagingCampaignMutation,
    useUpdateMessagingCampaignMutation,
    useCancelMessagingCampaignMutation,
    useDeleteMessagingCampaignMutation,
    useClearMessagingHistoryMutation,
    useTranslateMessagingMessageMutation,
    useSendMillenniumNewsMutation,
} from "../store/api";
import type { MessagingCampaign } from "../api/messaging";
import type { TelegramBotOption } from "../api/messaging";
import {
    toTelegramHtml,
    telegramHtmlToText,
    telegramHtmlToEditorHtml,
    telegramLimitStats,
    TG_ENTITY_LIMIT,
    TG_PHOTO_MAX_BYTES,
    TG_VIDEO_MAX_BYTES,
} from "../utils/telegramHtml";
import { TelegramPostPreview } from "./messaging/TelegramPostPreview";
import { adminAlert, adminConfirm, adminToast } from "../lib/adminDialog";

type MsgLang = "ru" | "en" | "uk" | "ka";

const MSG_LANGS: { id: MsgLang; label: string }[] = [
    { id: "ru", label: "RU" },
    { id: "en", label: "EN" },
    { id: "uk", label: "UA" },
    { id: "ka", label: "KA" },
];

const emptyMessages = (): Record<MsgLang, string> => ({
    ru: "",
    en: "",
    uk: "",
    ka: "",
});

const CATEGORY_LABELS: Record<number, string> = {
    1: "ВСЕМ пользователям с Telegram",
    2: "активным (оплаченным)",
    3: "Золотой 1000",
    4: "не удалённым",
    5: "удалённым",
    6: "меценатам",
    7: "акционерам",
    8: "тест (ID 1–3 и 12467)",
    9: "зарегистрированным из игры",
};

const CATEGORIES: { id: number; label: string }[] = [
    { id: 1, label: "Всем" },
    { id: 2, label: "Активным" },
    { id: 3, label: "Золотой 1000" },
    { id: 4, label: "Не удалённым" },
    { id: 5, label: "Удалённым" },
    { id: 6, label: "Меценатам" },
    { id: 7, label: "Акционерам" },
    { id: 8, label: "Тест" },
    { id: 9, label: "Из игры" },
];

const formatBytes = (n: number) =>
    n < 1048576
        ? `${Math.round(n / 1024)} КБ`
        : `${(n / 1048576).toFixed(1)} МБ`;

const chipClass = (active: boolean, danger = false) =>
    `px-3 py-1.5 text-sm rounded-md border whitespace-nowrap cursor-pointer transition-colors duration-200 ${
        active
            ? danger
                ? "bg-amber-600 text-white border-amber-600"
                : "bg-blue-600 text-white border-blue-600"
            : "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
    }`;

const GEORGIA_TZ = "Asia/Tbilisi";
const GEORGIA_OFFSET = "+04:00";

const partsInTz = (date: Date, timeZone: string) => {
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    });
    return Object.fromEntries(
        fmt
            .formatToParts(date)
            .filter((p) => p.type !== "literal")
            .map((p) => [p.type, p.value]),
    ) as Record<string, string>;
};

const toDatetimeLocalInGeorgia = (date: Date) => {
    const p = partsInTz(date, GEORGIA_TZ);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

const georgiaLocalToIso = (local: string) => {
    if (!local) return "";
    const stamp =
        local.length <= 16
            ? `${local}:00${GEORGIA_OFFSET}`
            : `${local}${GEORGIA_OFFSET}`;
    const d = new Date(stamp);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const isoToGeorgiaLocal = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return toDatetimeLocalInGeorgia(d);
};

const formatInTz = (iso: string, timeZone: string) =>
    new Date(iso).toLocaleString("ru-RU", {
        timeZone,
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

const formatWhen = (iso?: string | null) => {
    if (!iso) return "—";
    return `${formatInTz(iso, GEORGIA_TZ)} (Грузия)`;
};

const categoryShort = (id: number | null | undefined) => {
    if (id == null) return "—";
    return CATEGORIES.find((c) => c.id === id)?.label ?? `группа ${id}`;
};

const DRAFT_KEY = "alny.admin.messaging.draft";

type StudioTab = "editor" | "scheduled" | "history" | "templates";

const newMillExternalId = (): string => {
    const uuid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID().replace(/-/g, "")
            : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    return `msg_mill_${uuid}`.slice(0, 64);
};

export const AdminMessaging: React.FC = () => {
    const [tab, setTab] = useState<StudioTab>("editor");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [messagesByLang, setMessagesByLang] =
        useState<Record<MsgLang, string>>(emptyMessages);
    const [langTab, setLangTab] = useState<MsgLang>("ru");
    const [category, setCategory] = useState(8);
    const [isZoom, setIsZoom] = useState(false);
    const [zoomUrl, setZoomUrl] = useState(
        "https://us02web.zoom.us/j/83358831613?pwd=594760",
    );
    const [zoomCode, setZoomCode] = useState("594760");
    const [photo, setPhoto] = useState<File | null>(null);
    const [video, setVideo] = useState<File | null>(null);
    const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(
        null,
    );
    const [existingVideoUrl, setExistingVideoUrl] = useState<string | null>(
        null,
    );
    const [scheduledLocal, setScheduledLocal] = useState("");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingStatus, setEditingStatus] = useState<string | null>(null);
    const [sourceCampaignId, setSourceCampaignId] = useState<number | null>(
        null,
    );
    const [selectedBotKeys, setSelectedBotKeys] = useState<string[]>([]);
    const [botsInitialized, setBotsInitialized] = useState(false);
    const [millenniumCategory, setMillenniumCategory] = useState<
        "news" | "update" | "personal" | null
    >(null);
    const [millTitlesByLang, setMillTitlesByLang] =
        useState<Record<MsgLang, string>>(emptyMessages);
    const [millenniumRecipients, setMillenniumRecipients] = useState("");

    const [storeCampaign, { isLoading: isStoring }] =
        useStoreMessagingCampaignMutation();
    const [updateCampaign, { isLoading: isUpdating }] =
        useUpdateMessagingCampaignMutation();
    const [sendMillenniumNews, { isLoading: isSendingMillennium }] =
        useSendMillenniumNewsMutation();
    const [cancelCampaign] = useCancelMessagingCampaignMutation();
    const [deleteCampaign] = useDeleteMessagingCampaignMutation();
    const [clearHistory, { isLoading: isClearingHistory }] =
        useClearMessagingHistoryMutation();
    const [cancelBatch, { isLoading: isCancelling }] =
        useCancelMassMessageBatchMutation();
    const [translateMessage, { isLoading: isTranslating }] =
        useTranslateMessagingMessageMutation();

    const {
        data: telegramBotsResponse,
        isLoading: isLoadingBots,
        isError: isTelegramBotsError,
    } = useGetTelegramBotsQuery();
    const telegramBots = useMemo(() => {
        const raw = telegramBotsResponse as
            | { data?: TelegramBotOption[] }
            | TelegramBotOption[]
            | undefined;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return [];
    }, [telegramBotsResponse]);

    const { data: scheduledResponse } =
        useGetMessagingCampaignsQuery("scheduled");
    const { data: historyResponse } = useGetMessagingCampaignsQuery(
        "sent,sending,failed,cancelled",
        { pollingInterval: 4000 },
    );
    const { data: templatesResponse } =
        useGetMessagingCampaignsQuery("template");

    const scheduleHint = useMemo(() => {
        const iso = georgiaLocalToIso(scheduledLocal);
        if (!iso) return null;
        return {
            ge: formatInTz(iso, GEORGIA_TZ),
            utc: formatInTz(iso, "UTC"),
        };
    }, [scheduledLocal]);

    const draftReady = useRef(false);
    const langTabRef = useRef<MsgLang>(langTab);
    langTabRef.current = langTab;
    // lock до RTK isBusy — інакше два кліки / два confirm (інцидент #62/#63)
    const submittingRef = useRef(false);
    const [submitLock, setSubmitLock] = useState(false);
    // той самий external_id при retry після timeout (не новий uuid «всліпу»)
    const pendingMillExternalIdRef = useRef<string | null>(null);
    // mill уже ok у цій спробі submit — не слати знову (TG fail / A7)
    const millDoneThisSubmitRef = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: { openOnClick: false, autolink: true, linkOnPaste: true },
            }),
        ],
        content: "",
        onUpdate: ({ editor: ed }) => {
            const html = ed.getHTML();
            setMessage(html);
            setMessagesByLang((prev) => ({
                ...prev,
                [langTabRef.current]: html,
            }));
        },
        onSelectionUpdate: ({ editor: ed }) => setMessage(ed.getHTML()),
    });

    useEffect(() => {
        if (!editor || draftReady.current) return;
        draftReady.current = true;
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (!raw) return;
            const d = JSON.parse(raw) as {
                title?: string;
                html?: string;
                messagesByLang?: Partial<Record<MsgLang, string>>;
                langTab?: MsgLang;
                category?: number;
                scheduledLocal?: string;
                selectedBotKeys?: string[];
                millenniumCategory?: "news" | "update" | "personal" | null;
                millenniumRecipients?: string;
                millTitlesByLang?: Partial<Record<MsgLang, string>>;
                millenniumTitle?: string;
                isZoom?: boolean;
                zoomUrl?: string;
                zoomCode?: string;
            };
            if (d.title) setTitle(d.title);
            const loaded = emptyMessages();
            if (d.messagesByLang) {
                (Object.keys(loaded) as MsgLang[]).forEach((k) => {
                    if (d.messagesByLang?.[k]) loaded[k] = d.messagesByLang[k]!;
                });
            } else if (d.html) {
                loaded.ru = d.html;
            }
            setMessagesByLang(loaded);
            const titles = emptyMessages();
            if (d.millTitlesByLang) {
                (Object.keys(titles) as MsgLang[]).forEach((k) => {
                    if (d.millTitlesByLang?.[k]) titles[k] = d.millTitlesByLang[k]!;
                });
            } else if (d.millenniumTitle) {
                titles.ru = d.millenniumTitle;
            }
            setMillTitlesByLang(titles);
            const tab =
                d.langTab && loaded[d.langTab] !== undefined ? d.langTab : "ru";
            setLangTab(tab);
            if (loaded[tab]) {
                editor.commands.setContent(loaded[tab], { emitUpdate: false });
                setMessage(loaded[tab]);
            }
            if (typeof d.category === "number") setCategory(d.category);
            if (d.scheduledLocal) setScheduledLocal(d.scheduledLocal);
            if (
                Array.isArray(d.selectedBotKeys) &&
                d.selectedBotKeys.length > 0
            ) {
                setSelectedBotKeys(d.selectedBotKeys);
                setBotsInitialized(true);
            }
            if (
                d.millenniumCategory === "news" ||
                d.millenniumCategory === "update" ||
                d.millenniumCategory === "personal"
            ) {
                setMillenniumCategory(d.millenniumCategory);
            }
            if (typeof d.millenniumRecipients === "string") {
                setMillenniumRecipients(d.millenniumRecipients);
            }
            if (typeof d.isZoom === "boolean") setIsZoom(d.isZoom);
            if (d.zoomUrl) setZoomUrl(d.zoomUrl);
            if (d.zoomCode) setZoomCode(d.zoomCode);
        } catch {
            localStorage.removeItem(DRAFT_KEY);
        }
    }, [editor]);

    useEffect(() => {
        if (!draftReady.current) return;
        const timer = window.setTimeout(() => {
            const currentHtml = editor?.getHTML() ?? message;
            const snapshot = {
                ...messagesByLang,
                [langTab]: currentHtml,
            };
            localStorage.setItem(
                DRAFT_KEY,
                JSON.stringify({
                    title,
                    html: snapshot.ru,
                    messagesByLang: snapshot,
                    millTitlesByLang,
                    langTab,
                    category,
                    scheduledLocal,
                    selectedBotKeys,
                    millenniumCategory,
                    millenniumRecipients,
                    isZoom,
                    zoomUrl,
                    zoomCode,
                }),
            );
        }, 400);
        return () => window.clearTimeout(timer);
    }, [
        title,
        message,
        messagesByLang,
        millTitlesByLang,
        langTab,
        category,
        scheduledLocal,
        selectedBotKeys,
        millenniumCategory,
        millenniumRecipients,
        isZoom,
        zoomUrl,
        zoomCode,
        editor,
    ]);

    const photoPreviewUrl = useMemo(
        () => (photo ? URL.createObjectURL(photo) : existingPhotoUrl),
        [photo, existingPhotoUrl],
    );
    const hasMedia = Boolean(
        photo || video || existingPhotoUrl || existingVideoUrl,
    );
    const limits = telegramLimitStats(editor?.getHTML() ?? message, hasMedia);
    const limitOver = !isZoom && (limits.overChars || limits.overEntities);
    const limitPct = Math.min(100, (limits.chars / limits.limit) * 100);
    const limitBarClass =
        limits.overChars || limits.overEntities
            ? "bg-red-500"
            : limitPct >= 80
              ? "bg-amber-500"
              : "bg-blue-600";
    const limitTextClass =
        limits.overChars || limits.overEntities
            ? "text-red-600 dark:text-red-400"
            : limitPct >= 80
              ? "text-amber-600 dark:text-amber-400"
              : "text-gray-500 dark:text-gray-400";
    const isBusy =
        isStoring || isUpdating || isSendingMillennium || submitLock;
    const millTitleMissing =
        millenniumCategory !== null && !millTitlesByLang.ru.trim();
    const millRecipientsMissing =
        millenniumCategory === "personal" && !millenniumRecipients.trim();
    const millRuHtml =
        langTab === "ru"
            ? (editor?.getHTML() ?? messagesByLang.ru ?? "")
            : messagesByLang.ru || "";
    const millRuMissing =
        millenniumCategory !== null && !telegramHtmlToText(millRuHtml).trim();
    const selectedBotName =
        telegramBots.find((bot) => selectedBotKeys.includes(bot.key))?.name ||
        "Telegram";

    const toolbarButtons = [
        {
            mark: "bold",
            title: "Жирный",
            label: <strong>B</strong>,
            action: () => editor?.chain().focus().toggleBold().run(),
        },
        {
            mark: "italic",
            title: "Курсив",
            label: <em>I</em>,
            action: () => editor?.chain().focus().toggleItalic().run(),
        },
        {
            mark: "strike",
            title: "Зачёркнутый",
            label: <s>S</s>,
            action: () => editor?.chain().focus().toggleStrike().run(),
        },
        {
            mark: "code",
            title: "Моноширинный",
            label: <code>{"</>"}</code>,
            action: () => editor?.chain().focus().toggleCode().run(),
        },
        {
            mark: "link",
            title: "Ссылка на выделенном тексте",
            label: "🔗",
            action: () => {
                const url = window.prompt(
                    "Введите URL ссылки:",
                    editor?.getAttributes("link").href || "https://",
                );
                if (!url) return;
                editor?.chain().focus().setLink({ href: url }).run();
            },
        },
        {
            mark: "",
            title: "Убрать ссылку",
            label: "🔗×",
            action: () => editor?.chain().focus().unsetLink().run(),
        },
        {
            mark: "",
            title: "Убрать форматирование",
            label: "Tx",
            action: () =>
                editor?.chain().focus().unsetAllMarks().clearNodes().run(),
        },
    ];

    useEffect(() => {
        // ботів не авто-вибираємо: Telegram і Millennium — незалежні канали
        if (botsInitialized || telegramBots.length === 0) {
            return;
        }
        setBotsInitialized(true);
    }, [botsInitialized, telegramBots]);

    const toggleBotKey = (botKey: string) => {
        setSelectedBotKeys((current) =>
            current.includes(botKey)
                ? current.filter((key) => key !== botKey)
                : [...current, botKey],
        );
    };

    const pickPhoto = (file: File | null) => {
        if (file && file.size > TG_PHOTO_MAX_BYTES) {
            void adminAlert(
                `Фото больше ${formatBytes(TG_PHOTO_MAX_BYTES)} — Telegram не примет`,
                "Файл",
            );
            return;
        }
        setPhoto(file);
        if (file) {
            setVideo(null);
            setExistingVideoUrl(null);
            setExistingPhotoUrl(null);
        }
    };

    const pickVideo = (file: File | null) => {
        if (file && file.size > TG_VIDEO_MAX_BYTES) {
            void adminAlert(
                `Видео больше ${formatBytes(TG_VIDEO_MAX_BYTES)} — Telegram не примет`,
                "Файл",
            );
            return;
        }
        setVideo(file);
        if (file) {
            setPhoto(null);
            setExistingPhotoUrl(null);
            setExistingVideoUrl(null);
        }
    };

    const encodeMessageForFormData = (html: string): string =>
        btoa(unescape(encodeURIComponent(html)));

    const resetEditor = () => {
        setEditingId(null);
        setEditingStatus(null);
        setSourceCampaignId(null);
        setTitle("");
        editor?.commands.clearContent();
        setMessage("");
        setMessagesByLang(emptyMessages());
        setLangTab("ru");
        setPhoto(null);
        setVideo(null);
        setExistingPhotoUrl(null);
        setExistingVideoUrl(null);
        setScheduledLocal("");
        setIsZoom(false);
        setSelectedBotKeys([]);
        setMillenniumCategory(null);
        setMillTitlesByLang(emptyMessages());
        setMillenniumRecipients("");
        pendingMillExternalIdRef.current = null;
        millDoneThisSubmitRef.current = false;
        localStorage.removeItem(DRAFT_KEY);
    };

    const switchLangTab = (next: MsgLang) => {
        if (!editor || next === langTab) return;
        const currentHtml = editor.getHTML();
        const map = { ...messagesByLang, [langTab]: currentHtml };
        setMessagesByLang(map);
        if (langTab === "ru") setMessage(currentHtml);
        setLangTab(next);
        langTabRef.current = next;
        // без emitUpdate: false onUpdate затирає попередній таб порожнім HTML
        editor.commands.setContent(map[next] || "", { emitUpdate: false });
        setMessage(map[next] || "");
    };

    const translateCurrentTab = async () => {
        if (!editor || langTab === "ru") return;
        const map = {
            ...messagesByLang,
            [langTab]: editor.getHTML(),
        };
        const ruHtml = map.ru || "";
        const ruBody = telegramHtmlToText(ruHtml).trim();
        const ruTitle = millTitlesByLang.ru.trim();
        if (!ruBody && !ruTitle) {
            await adminAlert(
                "На вкладке RU нужен текст или заголовок в игре",
                "Перевод",
            );
            return;
        }
        try {
            if (ruBody) {
                const res = await translateMessage({
                    html: encodeMessageForFormData(toTelegramHtml(ruHtml)),
                    from: "ru",
                    to: langTab,
                    message_format: "b64",
                }).unwrap();
                if (!res.html) {
                    await adminAlert(res.message || "Пустой перевод", "Перевод");
                    return;
                }
                const editorHtml = telegramHtmlToEditorHtml(res.html);
                editor.commands.setContent(editorHtml, { emitUpdate: false });
                setMessagesByLang({ ...map, [langTab]: editorHtml });
                setMessage(editorHtml);
            }

            if (ruTitle) {
                const titleRes = await translateMessage({
                    html: encodeMessageForFormData(ruTitle),
                    from: "ru",
                    to: langTab,
                    message_format: "b64",
                }).unwrap();
                const translatedTitle = telegramHtmlToText(
                    titleRes.html || "",
                ).trim();
                if (translatedTitle) {
                    setMillTitlesByLang((prev) => ({
                        ...prev,
                        [langTab]: translatedTitle.slice(0, 255),
                    }));
                }
            }
        } catch (error: any) {
            await adminAlert(
                "Перевод не удался: " +
                    (error?.data?.message || error?.message || "нет ответа"),
                "Перевод",
            );
        }
    };

    const loadCampaign = (item: MessagingCampaign, asEdit: boolean) => {
        setEditingId(asEdit ? item.id : null);
        setEditingStatus(asEdit ? item.status : null);
        setSourceCampaignId(asEdit ? null : item.id);
        setTitle(item.title || "");
        const isMill = (item.bot_keys || []).includes("millennium");
        if (isMill) {
            // category=0 лише для Mill-історії — TG validator in:1..9 → "selected category is invalid"
            setCategory(item.category && item.category >= 1 ? item.category : 8);
            setSelectedBotKeys(
                (item.bot_keys || []).filter((k) => k !== "millennium"),
            );
            const metaCat = item.millennium_meta?.category;
            if (
                metaCat === "news" ||
                metaCat === "update" ||
                metaCat === "personal"
            ) {
                setMillenniumCategory(metaCat);
            } else {
                const err = item.error || "";
                setMillenniumCategory(
                    err.includes("Обновление")
                        ? "update"
                        : err.includes("Личное")
                          ? "personal"
                          : "news",
                );
            }
            if (item.millennium_meta?.recipients) {
                setMillenniumRecipients(item.millennium_meta.recipients);
            }
        } else {
            setCategory(item.category || 8);
            setSelectedBotKeys(item.bot_keys || []);
            setMillenniumCategory(null);
        }
        setIsZoom(Boolean(item.zoom));
        setZoomUrl(item.zoom_url || zoomUrl);
        setZoomCode(item.zoom_code || "");
        setScheduledLocal(isoToGeorgiaLocal(item.scheduled_at));
        setPhoto(null);
        setVideo(null);
        setExistingPhotoUrl(item.photo_url);
        setExistingVideoUrl(item.video_url);
        const loaded = emptyMessages();
        if (item.messages) {
            (Object.keys(loaded) as MsgLang[]).forEach((k) => {
                if (item.messages?.[k]) {
                    loaded[k] = telegramHtmlToEditorHtml(
                        item.messages[k] || "",
                    );
                }
            });
        }
        if (!loaded.ru && item.message) {
            loaded.ru = telegramHtmlToEditorHtml(item.message);
        }
        setMessagesByLang(loaded);
        const titles = emptyMessages();
        if (item.millennium_meta?.titles) {
            (Object.keys(titles) as MsgLang[]).forEach((k) => {
                if (item.millennium_meta?.titles?.[k]) {
                    titles[k] = item.millennium_meta.titles[k]!;
                }
            });
        } else if (isMill && item.title) {
            titles.ru = item.title;
        }
        setMillTitlesByLang(titles);
        setLangTab("ru");
        langTabRef.current = "ru";
        editor?.commands.setContent(loaded.ru || "", { emitUpdate: false });
        setMessage(loaded.ru || "");
        setTab("editor");
    };

    const tgBotKeys = selectedBotKeys.filter(
        (k) =>
            k !== "millennium" && telegramBots.some((b) => b.key === k),
    );

    const confirmBeforeSend = async (plainText: string): Promise<boolean> => {
        const botNames = telegramBots
            .filter((bot) => tgBotKeys.includes(bot.key))
            .map((bot) => bot.name);
        const audience = CATEGORY_LABELS[category] ?? `категория ${category}`;
        const milleniumLabel =
            millenniumCategory === "news"
                ? "Новость"
                : millenniumCategory === "update"
                  ? "Обновление"
                  : millenniumCategory === "personal"
                    ? "Личное"
                    : null;
        const wantTg = tgBotKeys.length > 0;
        const wantMill = millenniumCategory !== null;
        if (!wantTg && !wantMill) {
            await adminAlert(
                "Выберите Telegram бота и/или тип Millennium Новости",
                "Отправка",
            );
            return false;
        }
        if (
            !(await adminConfirm(
                [
                    wantTg && wantMill
                        ? "Отправка: Telegram + Millennium"
                        : wantTg
                          ? "Массовая рассылка в Telegram"
                          : "Отправка только в Millennium",
                    "",
                    wantTg ? `Аудитория Telegram: ${audience}` : null,
                    wantTg ? `Боты: ${botNames.join(", ") || "—"}` : null,
                    wantTg && tgBotKeys.length > 1
                        ? `Каждый получит ${tgBotKeys.length} сообщения (по одному на бота). Кто не стартовал бота — ошибка Telegram.`
                        : null,
                    wantMill && milleniumLabel
                        ? `Millennium: ${milleniumLabel}${
                              millenniumCategory === "personal"
                                  ? ` → ${millenniumRecipients.trim() || "—"}`
                                  : " (всем)"
                          }`
                        : null,
                    plainText ? `Текст: «${plainText.slice(0, 160)}»` : "",
                    "",
                    "Уже отправленные сообщения потом отменить нельзя.",
                ]
                    .filter((line) => line !== null)
                    .join("\n"),
                {
                    title: "Подтверждение отправки",
                    confirmText: "Отправить",
                    danger: true,
                },
            ))
        ) {
            return false;
        }
        // підтвердження TG-аудиторії — тільки якщо реально шлемо в ботів
        if (wantTg && category === 1) {
            return adminConfirm(
                "Отправить ВСЕМ пользователям с Telegram?",
                {
                    title: "Последнее предупреждение",
                    confirmText: "Да, всем",
                    danger: true,
                },
            );
        }
        if (wantTg && category !== 8 && CATEGORY_LABELS[category]) {
            return adminConfirm(
                `Точно отправить группе «${CATEGORY_LABELS[category]}»?`,
                { title: "Подтверждение", confirmText: "Отправить" },
            );
        }
        return true;
    };

    const buildFormData = (
        action: string,
        telegramMessage: string,
        allMessages: Record<MsgLang, string>,
        // millNow уже через sendMillenniumNews — у store тільки TG
        includeMill = true,
    ) => {
        const formData = new FormData();
        formData.append("action", action);
        formData.append(
            "category",
            (tgBotKeys.length > 0 ? category : 0).toString(),
        );
        formData.append("zoom", isZoom ? "1" : "0");
        if (title.trim()) formData.append("title", title.trim());
        tgBotKeys.forEach((botKey) => formData.append("bot_keys[]", botKey));
        if (includeMill && millenniumCategory) {
            formData.append("millennium_category", millenniumCategory);
            if (millenniumCategory === "personal") {
                formData.append(
                    "millennium_recipients",
                    millenniumRecipients.trim(),
                );
            }
            (Object.keys(millTitlesByLang) as MsgLang[]).forEach((lang) => {
                const t = millTitlesByLang[lang]?.trim() || "";
                if (t) formData.append(`titles[${lang}]`, t);
            });
        }
        if (isZoom) {
            formData.append("zoom_url", zoomUrl);
            if (zoomCode) formData.append("zoom_code", zoomCode);
        }
        formData.append("message_format", "b64");
        const ruTg = toTelegramHtml(allMessages.ru || telegramMessage);
        if (ruTg) {
            formData.append("message", encodeMessageForFormData(ruTg));
        }
        (Object.keys(allMessages) as MsgLang[]).forEach((lang) => {
            const tg = toTelegramHtml(allMessages[lang] || "");
            if (tg.trim()) {
                formData.append(
                    `messages[${lang}]`,
                    encodeMessageForFormData(tg),
                );
            }
        });
        if (scheduledLocal && (action === "schedule" || action === "save")) {
            formData.append("scheduled_at", georgiaLocalToIso(scheduledLocal));
        }
        if (photo) formData.append("photo", photo);
        else if (!existingPhotoUrl) formData.append("clear_photo", "1");
        if (video) formData.append("video", video);
        else if (!existingVideoUrl) formData.append("clear_video", "1");
        const mediaSourceId =
            sourceCampaignId ??
            (action === "template" && editingId ? editingId : null);
        if (mediaSourceId) {
            formData.append("source_id", String(mediaSourceId));
        }
        return formData;
    };

    const submit = async (
        action: "send" | "schedule" | "template" | "save",
    ) => {
        if (submittingRef.current) {
            return;
        }
        submittingRef.current = true;
        setSubmitLock(true);
        millDoneThisSubmitRef.current = false;

        try {
            const wantTelegram = tgBotKeys.length > 0;
            const wantMillennium = millenniumCategory !== null;
            // Mill у гру одразу лише на «Отправить»; schedule/template — через кампанію
            const millNow = wantMillennium && action === "send";
            const millDefer =
                wantMillennium &&
                (action === "schedule" ||
                    action === "template" ||
                    action === "save");

            if (action === "send") {
                if (!wantTelegram && !wantMillennium) {
                    await adminAlert(
                        "Выберите Telegram бота и/или тип Millennium Новости",
                        "Отправка",
                    );
                    return;
                }
            } else if (!wantTelegram && !wantMillennium) {
                await adminAlert(
                    "Выберите Telegram бота и/или тип Millennium",
                    "Отправка",
                );
                return;
            }
            if (wantTelegram && !CATEGORY_LABELS[category]) {
                await adminAlert(
                    "Выберите аудиторию Telegram (категорию получателей)",
                    "Отправка",
                );
                return;
            }
            if (
                wantMillennium &&
                millenniumCategory === "personal" &&
                !millenniumRecipients.trim()
            ) {
                await adminAlert(
                    "Для «Личное» укажите id или secret_cookie",
                    "Millennium",
                );
                return;
            }
            if (wantMillennium && !millTitlesByLang.ru.trim()) {
                await adminAlert(
                    "Для Millennium укажите заголовок RU (тайтл в игре)",
                    "Millennium",
                );
                return;
            }
            const editorHtml = editor?.getHTML() ?? message;
            const allMessages = {
                ...messagesByLang,
                [langTab]: editorHtml,
            };
            setMessagesByLang(allMessages);
            const telegramMessage = toTelegramHtml(allMessages.ru || editorHtml);
            const plainText = telegramHtmlToText(telegramMessage);
            const sendLimits = telegramLimitStats(
                allMessages.ru || editorHtml,
                hasMedia,
            );

            if (
                !isZoom &&
                !plainText &&
                !photo &&
                !video &&
                !existingPhotoUrl &&
                !existingVideoUrl
            ) {
                await adminAlert("Нужен текст (вкладка RU) или медиа", "Отправка");
                return;
            }
            if (wantTelegram && !isZoom && sendLimits.overChars) {
                await adminAlert(
                    `Текст длиннее лимита Telegram: ${sendLimits.chars} / ${sendLimits.limit}`,
                    "Лимит",
                );
                return;
            }
            if (wantTelegram && !isZoom && sendLimits.overEntities) {
                await adminAlert(
                    `Слишком много форматирования: ${sendLimits.entities} / ${TG_ENTITY_LIMIT}`,
                    "Лимит",
                );
                return;
            }
            if (action === "schedule" && !scheduledLocal) {
                await adminAlert(
                    "Выберите дату и время (Грузия, UTC+4)",
                    "Расписание",
                );
                return;
            }
            if (
                action === "save" &&
                editingStatus === "scheduled" &&
                !scheduledLocal
            ) {
                await adminAlert(
                    "Выберите дату и время (Грузия, UTC+4)",
                    "Расписание",
                );
                return;
            }
            if (action === "send" && !(await confirmBeforeSend(plainText))) {
                return;
            }
            if (
                action === "schedule" &&
                editingStatus !== "scheduled" &&
                !(await adminConfirm(
                    "Поставить в расписание на выбранное время?",
                    {
                        title: "Расписание",
                        confirmText: "Поставить",
                    },
                ))
            ) {
                return;
            }
            const notes: string[] = [];
            let millDone = false;
            const millExternalId =
                pendingMillExternalIdRef.current ?? newMillExternalId();
            if (millNow || millDefer) {
                pendingMillExternalIdRef.current = millExternalId;
            }

            try {
                if (millNow && millenniumCategory) {
                    if (millDoneThisSubmitRef.current) {
                        millDone = true;
                    } else {
                        const millBody = new FormData();
                        millBody.append("title", millTitlesByLang.ru.trim());
                        millBody.append(
                            "message",
                            encodeMessageForFormData(
                                allMessages.ru || editorHtml,
                            ),
                        );
                        millBody.append("message_format", "b64");
                        millBody.append(
                            "millennium_category",
                            millenniumCategory,
                        );
                        millBody.append("external_id", millExternalId);
                        (Object.keys(allMessages) as MsgLang[]).forEach(
                            (lang) => {
                                const t = millTitlesByLang[lang]?.trim() || "";
                                const body = allMessages[lang] || "";
                                if (t) millBody.append(`titles[${lang}]`, t);
                                if (telegramHtmlToText(body).trim()) {
                                    millBody.append(
                                        `messages[${lang}]`,
                                        encodeMessageForFormData(body),
                                    );
                                }
                            },
                        );
                        if (millenniumCategory === "personal") {
                            millBody.append(
                                "millennium_recipients",
                                millenniumRecipients.trim(),
                            );
                        }
                        if (photo) {
                            millBody.append("photo", photo);
                        } else if (existingPhotoUrl) {
                            millBody.append("image_url", existingPhotoUrl);
                        }
                        try {
                            const millResult =
                                await sendMillenniumNews(millBody).unwrap();
                            notes.push(millResult.message || "Millennium: ок");
                            millDone = true;
                            millDoneThisSubmitRef.current = true;
                            pendingMillExternalIdRef.current = null;
                        } catch (millErr: any) {
                            const status = millErr?.status;
                            const isNetwork =
                                status === "FETCH_ERROR" ||
                                status === "TIMEOUT_ERROR" ||
                                status === "PARSING_ERROR" ||
                                (!millErr?.data && !millErr?.status);
                            if (isNetwork) {
                                await adminAlert(
                                    [
                                        "Запрос в Millennium мог пройти (сеть/timeout).",
                                        `external_id: ${millExternalId}`,
                                        "Проверьте историю Messaging и админку Millennium перед повторной отправкой.",
                                        "Повтор с новым ключом создаст вторую новость.",
                                    ].join("\n"),
                                    "Внимание",
                                );
                            }
                            throw millErr;
                        }
                    }
                }

                // TG зараз / Mill у розклад (або обидва в одній кампанії)
                if ((wantTelegram && CATEGORY_LABELS[category]) || millDefer) {
                    // millNow: mill уже в грі — не тягнути millennium_category в store
                    const body = buildFormData(
                        action,
                        telegramMessage,
                        allMessages,
                        millDefer,
                    );
                    if (millDefer) {
                        body.append("external_id", millExternalId);
                    }
                    const canUpdate =
                        Boolean(editingId) &&
                        editingStatus === "scheduled" &&
                        (action === "save" ||
                            action === "schedule" ||
                            action === "send");
                    const result =
                        canUpdate && editingId !== null
                            ? await updateCampaign({
                                  id: editingId,
                                  body,
                              }).unwrap()
                            : await storeCampaign(body).unwrap();

                    notes.push(
                        action === "template"
                            ? "Добавлено в шаблоны. Откроется вкладка «Шаблоны»."
                            : action === "schedule"
                              ? millDefer
                                  ? "В расписании — уйдёт в игру" +
                                    (wantTelegram ? " и Telegram" : "") +
                                    " в выбранное время."
                                  : "Пост в расписании — уйдёт в выбранное время."
                              : result.message || "Telegram: готово",
                    );

                    if (action === "template") {
                        setEditingId(result.campaign?.id ?? editingId);
                        setEditingStatus(
                            result.campaign?.status ?? "template",
                        );
                        setTab("templates");
                    }
                    if (
                        action === "schedule" ||
                        (action === "save" && editingStatus === "scheduled")
                    ) {
                        setEditingId(result.campaign?.id ?? editingId);
                        setEditingStatus("scheduled");
                        setTab("scheduled");
                        pendingMillExternalIdRef.current = null;
                    }
                }

                adminToast(notes.join("\n") || "Готово", "success");
                if (action === "send") {
                    resetEditor();
                    setTab("history");
                }
            } catch (error: any) {
                const fieldErrors = error?.data?.errors;
                const firstFieldError = fieldErrors
                    ? (Object.values(fieldErrors).flat()[0] as
                          | string
                          | undefined)
                    : undefined;
                const raw =
                    (typeof firstFieldError === "string" &&
                    /scheduled_at|after/i.test(JSON.stringify(fieldErrors))
                        ? "Это время уже прошло по Грузии. Поставьте позже."
                        : firstFieldError) ||
                    error?.data?.message ||
                    error?.error ||
                    error?.message ||
                    "нет ответа от сервера";
                if (millDone) {
                    adminToast(notes.join("\n") || "Millennium: ок", "success");
                    if (action === "send") {
                        resetEditor();
                        setTab("history");
                    }
                    if (!/selected category is invalid/i.test(String(raw))) {
                        await adminAlert(`Telegram: ${raw}`, "Частично");
                    }
                    return;
                }
                await adminAlert(String(raw), "Ошибка");
            }
        } finally {
            submittingRef.current = false;
            setSubmitLock(false);
        }
    };

    const handleStopBatch = async (item: MessagingCampaign) => {
        if (!item.batch_id) return;
        if (
            !(await adminConfirm(
                "Сообщения, которые уже ушли в Telegram, отозвать нельзя.",
                {
                    title: "Остановить рассылку?",
                    confirmText: "Остановить",
                    danger: true,
                },
            ))
        ) {
            return;
        }
        try {
            const result = await cancelBatch(item.batch_id).unwrap();
            adminToast(result.message || "Остановлено", "success");
        } catch (error: any) {
            await adminAlert(
                error?.data?.message || error?.message || "нет ответа",
                "Ошибка",
            );
        }
    };

    const setPresetHours = (hours: number) => {
        setScheduledLocal(
            toDatetimeLocalInGeorgia(
                new Date(Date.now() + hours * 3600 * 1000),
            ),
        );
    };

    const tabs: {
        id: StudioTab;
        label: string;
        icon: React.ReactNode;
        count?: number;
    }[] = [
        {
            id: "editor",
            label: "Редактор",
            icon: <Pencil className="h-4 w-4" />,
        },
        {
            id: "scheduled",
            label: "Запланированные",
            icon: <Clock className="h-4 w-4" />,
            count: scheduledResponse?.data?.length,
        },
        {
            id: "history",
            label: "История",
            icon: <History className="h-4 w-4" />,
            count: historyResponse?.data?.length,
        },
        {
            id: "templates",
            label: "Шаблоны",
            icon: <Bookmark className="h-4 w-4" />,
            count: templatesResponse?.data?.length,
        },
    ];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Массовые рассылки
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Шаблон — заготовка, сам не уйдёт. В расписание — кнопка
                        «Запланировать».
                    </p>
                </div>
                {tab === "editor" && (
                    <button
                        type="button"
                        onClick={resetEditor}
                        className="text-sm text-gray-600 dark:text-gray-300 underline cursor-pointer"
                    >
                        Новый пост
                    </button>
                )}
            </div>

            <div className="flex flex-wrap gap-1 p-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                {tabs.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => setTab(item.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm cursor-pointer transition-colors duration-200 ${
                            tab === item.id
                                ? "bg-blue-600 text-white"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                    >
                        {item.icon}
                        {item.label}
                        {typeof item.count === "number" && item.count > 0 && (
                            <span className="text-[11px] opacity-80">
                                {item.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {tab === "editor" && (
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_410px] gap-4 items-start">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Название в истории
                            </label>
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Только для списка в админке, в игру не уходит"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div
                                className={`rounded-xl border p-3.5 space-y-3 ${
                                    tgBotKeys.length > 0
                                        ? "border-sky-400 bg-sky-50/60 dark:bg-sky-950/30 dark:border-sky-600"
                                        : "border-gray-200 dark:border-gray-700"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                        Telegram
                                    </h3>
                                    <span
                                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                            tgBotKeys.length > 0
                                                ? "bg-sky-600 text-white"
                                                : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                        }`}
                                    >
                                        {tgBotKeys.length > 0
                                            ? `${tgBotKeys.length} бот`
                                            : "выкл"}
                                    </span>
                                </div>
                                {isLoadingBots ? (
                                    <p className="text-sm text-gray-500">
                                        Загрузка…
                                    </p>
                                ) : isTelegramBotsError ? (
                                    <p className="text-sm text-red-600">
                                        Ошибка загрузки ботов
                                    </p>
                                ) : telegramBots.length === 0 ? (
                                    <p className="text-sm text-red-600">
                                        Нет ботов
                                    </p>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5">
                                        {telegramBots.map((bot) => (
                                            <button
                                                key={bot.key}
                                                type="button"
                                                onClick={() =>
                                                    toggleBotKey(bot.key)
                                                }
                                                className={chipClass(
                                                    selectedBotKeys.includes(
                                                        bot.key,
                                                    ),
                                                )}
                                            >
                                                {bot.name}
                                                {bot.is_default
                                                    ? " · default"
                                                    : ""}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {tgBotKeys.length > 0 && (
                                    <div>
                                        <div className="flex items-baseline justify-between gap-2 mb-1.5">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                                Кому в Telegram
                                            </label>
                                            <span className="text-[11px] text-gray-500 truncate max-w-[55%]">
                                                {CATEGORY_LABELS[category]}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {CATEGORIES.map((item) => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() =>
                                                        setCategory(item.id)
                                                    }
                                                    className={chipClass(
                                                        category === item.id,
                                                        item.id === 1,
                                                    )}
                                                >
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div
                                className={`rounded-xl border p-3.5 space-y-3 ${
                                    millenniumCategory
                                        ? "border-violet-400 bg-violet-50/60 dark:bg-violet-950/30 dark:border-violet-600"
                                        : "border-gray-200 dark:border-gray-700"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                        Millennium · игра
                                    </h3>
                                    <span
                                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                            millenniumCategory
                                                ? "bg-violet-600 text-white"
                                                : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                        }`}
                                    >
                                        {millenniumCategory === "news"
                                            ? "новость"
                                            : millenniumCategory === "update"
                                              ? "обновление"
                                              : millenniumCategory ===
                                                  "personal"
                                                ? "личное"
                                                : "выкл"}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(
                                        [
                                            ["news", "Новость"],
                                            ["update", "Обновление"],
                                            ["personal", "Личное"],
                                        ] as const
                                    ).map(([id, label]) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() =>
                                                setMillenniumCategory((cur) =>
                                                    cur === id ? null : id,
                                                )
                                            }
                                            className={chipClass(
                                                millenniumCategory === id,
                                            )}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                {millenniumCategory === "personal" && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                            Получатели{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            value={millenniumRecipients}
                                            onChange={(e) =>
                                                setMillenniumRecipients(
                                                    e.target.value,
                                                )
                                            }
                                            className={`w-full rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                                millRecipientsMissing
                                                    ? "border border-red-500"
                                                    : "border border-gray-300 dark:border-gray-600"
                                            }`}
                                            placeholder="id или secret_cookie"
                                        />
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-500">
                                    Повторный клик — снять. Можно вместе с
                                    Telegram.
                                </p>
                            </div>
                        </div>

                        <label className="inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isZoom}
                                onChange={(e) => setIsZoom(e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-gray-900 dark:text-white text-sm">
                                Zoom приглашение
                            </span>
                        </label>

                        {isZoom && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input
                                    type="url"
                                    value={zoomUrl}
                                    onChange={(e) => setZoomUrl(e.target.value)}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Zoom URL"
                                />
                                <input
                                    value={zoomCode}
                                    onChange={(e) =>
                                        setZoomCode(e.target.value)
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Код доступа"
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Фото до {formatBytes(TG_PHOTO_MAX_BYTES)}
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        pickPhoto(e.target.files?.[0] || null)
                                    }
                                    className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700"
                                />
                                {(photo || existingPhotoUrl) && (
                                    <button
                                        type="button"
                                        className="mt-1 text-xs text-red-600 cursor-pointer"
                                        onClick={() => {
                                            setPhoto(null);
                                            setExistingPhotoUrl(null);
                                        }}
                                    >
                                        убрать фото
                                    </button>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Видео до {formatBytes(TG_VIDEO_MAX_BYTES)}
                                    {millenniumCategory ? (
                                        <span className="ml-1 text-xs font-normal text-gray-400">
                                            (только Telegram)
                                        </span>
                                    ) : null}
                                </label>
                                <input
                                    type="file"
                                    accept="video/*"
                                    onChange={(e) =>
                                        pickVideo(e.target.files?.[0] || null)
                                    }
                                    className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700"
                                />
                            </div>
                        </div>

                        <div
                            className={`rounded-xl border p-3.5 space-y-3 ${
                                millenniumCategory
                                    ? "border-violet-300 dark:border-violet-700"
                                    : "border-gray-200 dark:border-gray-700"
                            }`}
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white mr-1">
                                        Текст
                                        {millenniumCategory ? (
                                            <span className="text-red-500">
                                                {" "}
                                                *
                                            </span>
                                        ) : null}
                                    </span>
                                    {MSG_LANGS.map((lang) => {
                                        const filled = Boolean(
                                            telegramHtmlToText(
                                                lang.id === langTab
                                                    ? editor?.getHTML() ||
                                                          messagesByLang[
                                                              lang.id
                                                          ]
                                                    : messagesByLang[lang.id] ||
                                                          "",
                                            ).trim() ||
                                                (millenniumCategory &&
                                                    millTitlesByLang[
                                                        lang.id
                                                    ]?.trim()),
                                        );
                                        return (
                                            <button
                                                key={lang.id}
                                                type="button"
                                                onClick={() =>
                                                    switchLangTab(lang.id)
                                                }
                                                className={`min-w-[2.5rem] px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer ${
                                                    langTab === lang.id
                                                        ? "bg-blue-600 text-white shadow-sm"
                                                        : filled
                                                          ? "bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800"
                                                          : "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                                                }`}
                                            >
                                                {lang.label}
                                                {filled ? " ✓" : ""}
                                            </button>
                                        );
                                    })}
                                    {langTab !== "ru" && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                void translateCurrentTab()
                                            }
                                            disabled={isTranslating}
                                            className="ml-0.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 cursor-pointer disabled:opacity-50"
                                        >
                                            {isTranslating
                                                ? "…"
                                                : `RU → ${
                                                      MSG_LANGS.find(
                                                          (l) =>
                                                              l.id === langTab,
                                                      )?.label || langTab
                                                  }`}
                                        </button>
                                    )}
                                </div>
                                <span
                                    className={`text-xs tabular-nums ${limitTextClass}`}
                                >
                                    {limits.chars} / {limits.limit}
                                    {hasMedia ? " · подпись" : ""}
                                </span>
                            </div>

                            {millenniumCategory && (
                                <div>
                                    <label className="block text-xs font-medium text-violet-800 dark:text-violet-200 mb-1">
                                        Заголовок в игре{" "}
                                        <span className="text-red-500">*</span>
                                        <span className="ml-1 font-normal opacity-70">
                                            ·{" "}
                                            {MSG_LANGS.find(
                                                (l) => l.id === langTab,
                                            )?.label || langTab}
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        value={millTitlesByLang[langTab] || ""}
                                        onChange={(e) =>
                                            setMillTitlesByLang((prev) => ({
                                                ...prev,
                                                [langTab]: e.target.value,
                                            }))
                                        }
                                        maxLength={255}
                                        className={`w-full rounded-md px-3 py-2.5 text-[15px] font-medium bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                                            langTab === "ru" && millTitleMissing
                                                ? "border border-red-500"
                                                : "border border-violet-200 dark:border-violet-700"
                                        }`}
                                        placeholder="Тайтл карточки в ленте Millennium"
                                    />
                                </div>
                            )}

                            <div
                                className={`rounded-md overflow-hidden bg-white dark:bg-gray-700 ${
                                    millRuMissing
                                        ? "border border-red-500"
                                        : "border border-gray-300 dark:border-gray-600"
                                }`}
                            >
                                <div className="flex flex-wrap gap-1 p-1.5 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                                    {toolbarButtons.map((button) => {
                                        const active =
                                            Boolean(button.mark) &&
                                            Boolean(
                                                editor?.isActive(button.mark),
                                            );
                                        return (
                                            <button
                                                key={button.title}
                                                type="button"
                                                title={button.title}
                                                onClick={button.action}
                                                className={`min-w-[2rem] px-2 py-1 rounded text-sm cursor-pointer ${
                                                    active
                                                        ? "bg-blue-600 text-white"
                                                        : "border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                                                }`}
                                            >
                                                {button.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="px-3 py-2">
                                    <EditorContent editor={editor} />
                                    <style>{`
                                            .ProseMirror { outline: none; min-height: 160px; }
                                            .ProseMirror a { color: #2563eb; text-decoration: underline; }
                                            .ProseMirror p { margin: 0.25em 0; }
                                        `}</style>
                                </div>
                                <div className="h-1 bg-gray-200 dark:bg-gray-600">
                                    <div
                                        className={`h-1 ${limitBarClass}`}
                                        style={{ width: `${limitPct}%` }}
                                    />
                                </div>
                            </div>
                            <div className="mt-1 flex justify-between text-xs text-gray-500">
                                <span>
                                    {hasMedia
                                        ? "С медиа текст уйдёт как подпись (1024)."
                                        : "Без медиа — сообщение (4096)."}
                                </span>
                                <span>
                                    форматирование {limits.entities}/
                                    {TG_ENTITY_LIMIT}
                                </span>
                            </div>
                        </div>

                        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3 space-y-2">
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                Когда отправить — Грузия (Tbilisi, UTC+4)
                            </div>
                            <p className="text-xs text-gray-500">
                                Ставьте дату и час как в Тбилиси. Сервер хранит
                                UTC (на 4 часа меньше).
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className={chipClass(false)}
                                    onClick={() => setPresetHours(1)}
                                >
                                    +1 час
                                </button>
                                <button
                                    type="button"
                                    className={chipClass(false)}
                                    onClick={() => setPresetHours(3)}
                                >
                                    +3 часа
                                </button>
                                <button
                                    type="button"
                                    className={chipClass(false)}
                                    onClick={() => {
                                        const p = partsInTz(
                                            new Date(),
                                            GEORGIA_TZ,
                                        );
                                        const today = new Date(
                                            `${p.year}-${p.month}-${p.day}T00:00:00${GEORGIA_OFFSET}`,
                                        );
                                        const tomorrow10 = new Date(
                                            today.getTime() + 34 * 3600 * 1000,
                                        );
                                        setScheduledLocal(
                                            toDatetimeLocalInGeorgia(
                                                tomorrow10,
                                            ),
                                        );
                                    }}
                                >
                                    завтра 10:00 (Грузия)
                                </button>
                            </div>
                            <input
                                type="datetime-local"
                                value={scheduledLocal}
                                onChange={(e) =>
                                    setScheduledLocal(e.target.value)
                                }
                                className="w-full max-w-xs border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                            {scheduleHint && (
                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                    В Грузии в этот момент:{" "}
                                    <b>{scheduleHint.ge}</b>
                                    <br />
                                    UTC в этот момент: {scheduleHint.utc}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => submit("send")}
                                disabled={
                                    isBusy ||
                                    (selectedBotKeys.length > 0 && limitOver)
                                }
                                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                            >
                                <Send className="h-4 w-4" />
                                {isBusy ? "Отправка..." : "Отправить сейчас"}
                            </button>
                            <button
                                type="button"
                                onClick={() => submit("schedule")}
                                disabled={isBusy || limitOver}
                                className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md disabled:opacity-50 cursor-pointer"
                            >
                                <Clock className="h-4 w-4" />
                                {editingStatus === "scheduled"
                                    ? "Сохранить в расписании"
                                    : "Запланировать"}
                            </button>
                            <button
                                type="button"
                                onClick={() => submit("template")}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1.5 border border-gray-300 dark:border-gray-500 px-4 py-2 rounded-md text-gray-800 dark:text-gray-100 cursor-pointer"
                            >
                                <Bookmark className="h-4 w-4" />
                                Добавить в шаблоны
                            </button>
                            {editingId && editingStatus !== "scheduled" && (
                                <button
                                    type="button"
                                    onClick={() => submit("save")}
                                    disabled={isBusy}
                                    className="px-4 py-2 rounded-md text-sm text-gray-600 dark:text-gray-300 cursor-pointer"
                                >
                                    Сохранить правки
                                </button>
                            )}
                        </div>
                        {category === 1 && (
                            <p className="text-xs text-amber-600">
                                «Всем» — перед отправкой будет двойное
                                подтверждение
                            </p>
                        )}
                    </div>

                    <div className="xl:sticky xl:top-4 space-y-3">
                        <TelegramPostPreview
                            html={editor?.getHTML() ?? message}
                            photoUrl={photoPreviewUrl}
                            videoName={
                                video?.name ||
                                (existingVideoUrl ? "видео" : null)
                            }
                            botName={selectedBotName}
                            isZoom={isZoom}
                        />
                    </div>
                </div>
            )}

            {tab === "scheduled" && (
                <CampaignList
                    items={scheduledResponse?.data || []}
                    botOptions={telegramBots}
                    empty="Ничего не запланировано. Шаблоны сюда не попадают."
                    onOpen={(item) => loadCampaign(item, true)}
                    onCancel={async (item) => {
                        if (
                            !(await adminConfirm("Пост уже не уйдёт.", {
                                title: "Снять с расписания?",
                                confirmText: "Снять",
                                danger: true,
                            }))
                        )
                            return;
                        await cancelCampaign(item.id).unwrap();
                        adminToast("Снято с расписания", "success");
                    }}
                    onDelete={async (item) => {
                        if (
                            !(await adminConfirm("Удалить запись?", {
                                title: "Удаление",
                                confirmText: "Удалить",
                                danger: true,
                            }))
                        )
                            return;
                        try {
                            await deleteCampaign(item.id).unwrap();
                            adminToast("Удалено", "success");
                        } catch (error: any) {
                            await adminAlert(
                                error?.data?.message ||
                                    error?.message ||
                                    "нет ответа",
                                "Ошибка",
                            );
                        }
                    }}
                />
            )}
            {tab === "history" && (
                <div className="space-y-3">
                    {(historyResponse?.data?.length ?? 0) > 0 && (
                        <div className="flex justify-end">
                            <button
                                type="button"
                                disabled={isClearingHistory}
                                onClick={async () => {
                                    if (
                                        !(await adminConfirm(
                                            "Тестовые записи пропадут, шаблоны и расписание останутся.",
                                            {
                                                title: "Очистить историю?",
                                                confirmText: "Очистить",
                                                danger: true,
                                            },
                                        ))
                                    ) {
                                        return;
                                    }
                                    try {
                                        await clearHistory().unwrap();
                                        adminToast("История очищена", "success");
                                    } catch (error: any) {
                                        await adminAlert(
                                            error?.data?.message ||
                                                error?.message ||
                                                "нет ответа",
                                            "Ошибка",
                                        );
                                    }
                                }}
                                className="text-sm text-red-600 inline-flex items-center gap-1 cursor-pointer disabled:opacity-50"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                {isClearingHistory
                                    ? "Очищаю..."
                                    : "Очистить историю"}
                            </button>
                        </div>
                    )}
                    <CampaignList
                        items={historyResponse?.data || []}
                        botOptions={telegramBots}
                        empty="История пуста — отправленные посты появятся здесь"
                        onOpen={(item) => loadCampaign(item, false)}
                        onStop={handleStopBatch}
                        stopping={isCancelling}
                        onDelete={async (item) => {
                            if (
                            !(await adminConfirm(
                                (item.bot_keys || []).includes("millennium")
                                    ? item.millennium_news_id
                                        ? "Удалить и в Millennium, и из истории?"
                                        : "В истории нет mill id — уйдёт только из HUB (в игре может остаться)."
                                    : "Удалить из истории?",
                                {
                                    title: "Удаление",
                                    confirmText: "Удалить",
                                    danger: true,
                                },
                            ))
                        )
                            return;
                        try {
                            await deleteCampaign(item.id).unwrap();
                            adminToast("Удалено", "success");
                        } catch (error: any) {
                            await adminAlert(
                                error?.data?.message ||
                                    error?.message ||
                                    "нет ответа",
                                "Ошибка",
                            );
                        }
                        }}
                    />
                </div>
            )}
            {tab === "templates" && (
                <CampaignList
                    items={templatesResponse?.data || []}
                    botOptions={telegramBots}
                    empty="Нет шаблонов. В редакторе — «Добавить в шаблоны»."
                    onOpen={(item) => loadCampaign(item, false)}
                    onDelete={async (item) => {
                        if (
                            !(await adminConfirm("Удалить шаблон?", {
                                title: "Удаление",
                                confirmText: "Удалить",
                                danger: true,
                            }))
                        )
                            return;
                        try {
                            await deleteCampaign(item.id).unwrap();
                            adminToast("Шаблон удалён", "success");
                        } catch (error: any) {
                            await adminAlert(
                                error?.data?.message ||
                                    error?.message ||
                                    "нет ответа",
                                "Ошибка",
                            );
                        }
                    }}
                />
            )}
        </div>
    );
};

const STATUS_LABEL: Record<string, string> = {
    scheduled: "в расписании",
    sent: "отправлено",
    sending: "в очереди",
    template: "шаблон",
    cancelled: "отменено",
    failed: "ошибка",
};

const CampaignBatchBar: React.FC<{
    batchId: string;
    campaignStatus?: string;
}> = ({ batchId, campaignStatus }) => {
    const campaignDone = ["sent", "failed", "cancelled"].includes(
        campaignStatus || "",
    );
    const skip = !batchId || batchId === "inline";
    const { data } = useGetBatchStatusQuery(batchId, {
        pollingInterval: campaignDone ? 0 : 2000,
        skip,
    });
    if (skip) {
        return null;
    }
    const s = data?.data;
    if (!s) {
        return <p className="text-[11px] text-gray-400">статус очереди…</p>;
    }

    // job_batches інколи лишає pending після фейлів — статус кампанії надійніший
    const done = campaignDone || Boolean(s.finished) || Boolean(s.cancelled);
    const failed = s.failed ?? 0;
    const total = s.total ?? 0;
    const delivered = done
        ? Math.max(0, total - failed)
        : Math.max(0, s.delivered ?? (s.processed ?? 0) - failed);
    const noneOk = done && total > 0 && failed === total;
    const barClass = done
        ? noneOk
            ? "bg-red-500"
            : "bg-emerald-600"
        : failed
          ? "bg-amber-500"
          : "bg-blue-600";

    let line: string;
    if (s.cancelled || campaignStatus === "cancelled") {
        line = `Остановлено · доставлено ${delivered} сообщ. из ${total}`;
    } else if (done) {
        line = noneOk
            ? `Завершено · ничего не доставлено (0 сообщ. из ${total})`
            : `Завершено · доставлено ${delivered} сообщ. из ${total}`;
        if (!noneOk && failed) {
            line += ` · не дошло ${failed}`;
        }
    } else {
        line = `Идёт · ${s.processed}/${total} · доставлено ${delivered} сообщ.`;
        if (failed) {
            line += ` · не дошло ${failed}`;
        }
    }

    return (
        <div className="space-y-1">
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                <div
                    className={`h-1.5 rounded-full ${barClass}`}
                    style={{
                        width: `${done ? 100 : Math.min(100, s.progress)}%`,
                    }}
                />
            </div>
            <p className="text-[11px] text-gray-500">{line}</p>
        </div>
    );
};

const MILLENNIUM_TYPE_LABEL: Record<string, string> = {
    news: "Новость",
    update: "Обновление",
    personal: "Личное",
};

const millTypeFromCampaign = (item: MessagingCampaign): string | null => {
    const meta = item.millennium_meta?.category;
    if (meta && MILLENNIUM_TYPE_LABEL[meta]) {
        return MILLENNIUM_TYPE_LABEL[meta];
    }
    if (!(item.bot_keys || []).includes("millennium")) {
        return null;
    }
    const err = item.error || "";
    if (err.includes("Обновление")) return "Обновление";
    if (err.includes("Личное")) return "Личное";
    return "Новость";
};

const CampaignList: React.FC<{
    items: MessagingCampaign[];
    botOptions?: TelegramBotOption[];
    empty: string;
    onOpen: (item: MessagingCampaign) => void;
    onCancel?: (item: MessagingCampaign) => void;
    onStop?: (item: MessagingCampaign) => void;
    stopping?: boolean;
    onDelete?: (item: MessagingCampaign) => void;
}> = ({
    items,
    botOptions = [],
    empty,
    onOpen,
    onCancel,
    onStop,
    stopping,
    onDelete,
}) => {
    if (items.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center text-gray-500">
                {empty}
            </div>
        );
    }

    const botName = (key: string) =>
        botOptions.find((b) => b.key === key)?.name || key;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {items.map((item) => {
                const millType = millTypeFromCampaign(item);
                const tgKeys = (item.bot_keys || []).filter(
                    (k) => k !== "millennium",
                );
                const hasMill = Boolean(millType);
                const hasTg = tgKeys.length > 0;

                return (
                    <article
                        key={item.id}
                        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
                    >
                        {item.photo_url && (
                            <img
                                src={item.photo_url}
                                alt=""
                                className="w-full h-28 object-cover"
                            />
                        )}
                        <div className="p-3 space-y-2 flex-1 flex flex-col">
                            <div className="flex items-start justify-between gap-2">
                                <h3 className="font-medium text-gray-900 dark:text-white line-clamp-2">
                                    {item.title || "Без названия"}
                                </h3>
                                <span
                                    className={`text-[11px] shrink-0 ${
                                        item.status === "failed"
                                            ? "text-red-600"
                                            : "text-gray-500"
                                    }`}
                                >
                                    {STATUS_LABEL[item.status] || item.status}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-3">
                                {telegramHtmlToText(item.message || "") || "—"}
                            </p>
                            <p className="text-[11px] text-gray-400">
                                {item.scheduled_at
                                    ? formatWhen(item.scheduled_at)
                                    : formatWhen(
                                          item.sent_at || item.created_at,
                                      )}
                            </p>
                            {item.error && item.status === "failed" && (
                                <p className="text-xs break-words text-red-600">
                                    {item.error}
                                </p>
                            )}
                            {item.batch_id && item.batch_id !== "inline" && (
                                <CampaignBatchBar
                                    batchId={item.batch_id}
                                    campaignStatus={item.status}
                                />
                            )}
                            {hasTg &&
                                (item.users_count != null ||
                                    item.jobs_count != null) && (
                                    <p className="text-[11px] text-gray-400">
                                        {item.users_count != null &&
                                        (item.bots_count ?? 0) > 1 &&
                                        item.jobs_count != null
                                            ? `${item.users_count} чел. × ${item.bots_count} бота = ${item.jobs_count} сообщ.`
                                            : [
                                                  item.users_count != null
                                                      ? `${item.users_count} чел.`
                                                      : null,
                                                  item.jobs_count != null
                                                      ? `${item.jobs_count} сообщ.`
                                                      : null,
                                              ]
                                                  .filter(Boolean)
                                                  .join(" · ")}
                                    </p>
                                )}
                            <div className="mt-auto pt-2 border-t border-gray-100 dark:border-gray-700 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                                    Куда
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {hasMill && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                                            Millennium · {millType}
                                            {item.millennium_news_id
                                                ? ` #${item.millennium_news_id}`
                                                : ""}
                                        </span>
                                    )}
                                    {tgKeys.map((key) => (
                                        <span
                                            key={key}
                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                                        >
                                            {botName(key)}
                                        </span>
                                    ))}
                                    {hasTg &&
                                        CATEGORY_LABELS[item.category] && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                                {categoryShort(item.category)}
                                            </span>
                                        )}
                                    {!hasMill && !hasTg && (
                                        <span className="text-[11px] text-gray-400">
                                            —
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => onOpen(item)}
                                    className="text-sm text-blue-600 cursor-pointer"
                                >
                                    {item.status === "template"
                                        ? "Использовать шаблон"
                                        : item.status === "scheduled"
                                          ? "Править"
                                          : "Открыть в редакторе"}
                                </button>
                                {onCancel && (
                                    <button
                                        type="button"
                                        onClick={() => onCancel(item)}
                                        className="text-sm text-amber-700 cursor-pointer"
                                    >
                                        Снять с расписания
                                    </button>
                                )}
                                {onStop &&
                                    item.status === "sending" &&
                                    item.batch_id && (
                                        <button
                                            type="button"
                                            onClick={() => onStop(item)}
                                            disabled={stopping}
                                            className="text-sm text-red-600 cursor-pointer disabled:opacity-50"
                                        >
                                            {stopping
                                                ? "Остановка..."
                                                : "Остановить"}
                                        </button>
                                    )}
                                {onDelete && (
                                    <button
                                        type="button"
                                        onClick={() => onDelete(item)}
                                        className="text-sm text-red-600 inline-flex items-center gap-1 cursor-pointer"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Удалить
                                    </button>
                                )}
                            </div>
                        </div>
                    </article>
                );
            })}
        </div>
    );
};
